import { useCallback, useEffect, useRef, useState } from "react";
import type { Kind } from "@norkron/web-review-workbench";
import type {
  ResourceRole,
  Section,
  SectionLink,
} from "../../api/articleUnits";
import { GripVertical } from "../../compose/Icon";
import {
  type BlockRectSource,
  sectionOrdAt,
  useCanvasSectionRegions,
} from "../../lib/canvasSectionRegions";
import { SectionHandlePopover } from "./SectionHandlePopover";

/**
 * `<SectionHandleLayer>` — the Google-Docs / Notion margin grip that replaces both
 * killed chip rows (design §2). One quiet handle per carved section, hung in the
 * paper's LEFT margin (outside the text column, so it can never collide with text
 * selection / the comment composer — §5), revealed for the section the reviewer is
 * hovering or has focused. Each grip carries the section's `v{n}` + a state dot
 * (amber = stale link pin, blue = advanced past v1, none = clean) and opens the
 * `<SectionHandlePopover>` — the single entry point to compare / history / the
 * cross-resource link + Re-pin / comment.
 *
 * Mounted INSIDE `<DoczCanvas>`'s read surface (`.wb-paper`, the positioning
 * context) so it shares the canvas ref and measures block offsets. The grips form
 * a roving-tabindex toolbar (§6): arrow keys + `[`/`]` move between sections,
 * Enter/Space opens the popover.
 */

export interface SectionHandleLayerProps {
  readonly kind: Kind;
  readonly sections: readonly Section[];
  /** The painted READ CANVAS. Sections are placed by asking it for the viewport
   *  rect of each section's first block — there are no DOM blocks to measure. */
  readonly surfaceRef: React.RefObject<BlockRectSource | null>;
  /** The document body — recompute placements when it repaints. */
  readonly paintKey: string;
  /** Whether the pane is shown in READ mode (the layer no-ops otherwise). */
  readonly active: boolean;
  /** Cross-resource links (article pane hub, design §1.5). */
  readonly links: readonly SectionLink[];
  readonly onCompareSection: (section: Section) => void;
  readonly onRepin: (linkId: number) => void;
  readonly onLinkCounterpart: (role: ResourceRole) => void;
  readonly onCommentSection: (section: Section) => void;
}

/** The head version seq the handle badges (design §2.4 — `headSeq()`, the same the
 *  dead `SectionVersionChips` used). */
function headSeq(section: Section): number {
  if (section.versions.length > 0) {
    return section.versions[section.versions.length - 1].seq;
  }
  return 1;
}

/** The link that pins this section to its counterpart (article pane only). Matches
 *  on the section id, then the heading (the mock keys on heading). */
function linkFor(
  section: Section,
  kind: Kind,
  links: readonly SectionLink[],
): SectionLink | null {
  if (kind !== "article") return null;
  return (
    links.find(
      (l) =>
        String(l.article_section_id) === section.id ||
        l.article_heading === section.heading,
    ) ?? null
  );
}

export function SectionHandleLayer({
  kind,
  sections,
  surfaceRef,
  paintKey,
  active,
  links,
  onCompareSection,
  onRepin,
  onLinkCounterpart,
  onCommentSection,
}: SectionHandleLayerProps) {
  // The layer is absolutely positioned inside `.wb-paper`, so it IS the
  // coordinate space the grips are placed in — measure against itself.
  const layerRef = useRef<HTMLDivElement>(null);
  const regions = useCanvasSectionRegions(
    surfaceRef,
    layerRef,
    sections,
    active,
    paintKey,
  );
  const [hoveredOrd, setHoveredOrd] = useState<number | null>(null);
  const [focusedOrd, setFocusedOrd] = useState<number | null>(null);
  const [popoverOrd, setPopoverOrd] = useState<number | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [roving, setRoving] = useState(0);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gripRefs = useRef(new Map<number, HTMLButtonElement>());

  // `computeCanvasSectionRegions` already drops sections the surface could not
  // locate — an unplaced grip is worse than none, because it looks placed.
  const placed = regions;

  const activeOrd = popoverOrd ?? hoveredOrd ?? focusedOrd;

  const clearClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  const scheduleClose = useCallback(() => {
    clearClose();
    closeTimer.current = setTimeout(() => setHoveredOrd(null), 200);
  }, [clearClose]);

  // Pointer reveal: hovering anywhere in a section's vertical BAND reveals THAT
  // section's handle. The DOM version asked which `.docz-block` was under the
  // pointer; a canvas has no blocks to hit, so the question becomes "which
  // measured band is this Y inside" — the same answer, one coordinate down.
  //
  // Bound to the paper (the layer's offsetParent) rather than to the canvas, so
  // the margin the grips live in counts as part of the section too — the grip
  // does not vanish the moment the pointer leaves the text column to reach it.
  useEffect(() => {
    const host = layerRef.current?.parentElement ?? null;
    if (!host || !active || regions.length === 0) return;
    const onMove = (e: MouseEvent) => {
      const ord = sectionOrdAt(
        regions,
        e.clientY - host.getBoundingClientRect().top,
      );
      if (ord != null) {
        clearClose();
        setHoveredOrd(ord);
      }
    };
    const onLeave = () => scheduleClose();
    host.addEventListener("mousemove", onMove);
    host.addEventListener("mouseleave", onLeave);
    return () => {
      host.removeEventListener("mousemove", onMove);
      host.removeEventListener("mouseleave", onLeave);
    };
  }, [active, regions, clearClose, scheduleClose]);

  useEffect(() => () => clearClose(), [clearClose]);

  const openPopover = useCallback((ord: number, grip: HTMLButtonElement) => {
    setAnchorRect(grip.getBoundingClientRect());
    setPopoverOrd(ord);
  }, []);

  const closePopover = useCallback(() => {
    setPopoverOrd(null);
    setAnchorRect(null);
  }, []);

  const focusGrip = useCallback((ord: number) => {
    const g = gripRefs.current.get(ord);
    g?.focus();
  }, []);

  const onGripKeyDown = useCallback(
    (e: React.KeyboardEvent, ord: number, idx: number) => {
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        openPopover(ord, e.currentTarget as HTMLButtonElement);
        return;
      }
      if (e.key === "ArrowDown" || e.key === "]") {
        e.preventDefault();
        const next = Math.min(placed.length - 1, idx + 1);
        setRoving(next);
        focusGrip(placed[next].section.ord);
        return;
      }
      if (e.key === "ArrowUp" || e.key === "[") {
        e.preventDefault();
        const prev = Math.max(0, idx - 1);
        setRoving(prev);
        focusGrip(placed[prev].section.ord);
      }
    },
    [openPopover, placed, focusGrip],
  );

  if (!active || placed.length === 0) return null;

  const popoverRegion =
    popoverOrd != null
      ? placed.find((r) => r.section.ord === popoverOrd) ?? null
      : null;

  return (
    <div
      ref={layerRef}
      className="wb-shandles"
      role="toolbar"
      aria-label={`${kind} section handles`}
      aria-orientation="vertical"
      data-testid={`section-handles-${kind}`}
    >
      {placed.map((region, idx) => {
        const section = region.section;
        const seq = headSeq(section);
        const link = linkFor(section, kind, links);
        const stale = link != null && (link.stale_other || link.stale_article);
        const dot = stale ? "stale" : seq > 1 ? "advanced" : "none";
        const isActive = activeOrd === section.ord;
        const top = region.top;
        const staleSuffix =
          stale && link
            ? `, stale — refers to ${link.other_role} v${link.other_pin_seq}`
            : "";
        return (
          <button
            key={section.id}
            type="button"
            ref={(node) => {
              if (node) gripRefs.current.set(section.ord, node);
              else gripRefs.current.delete(section.ord);
            }}
            className="wb-shandle"
            data-testid={`section-handle-${kind}-${idx}`}
            data-active={isActive ? "true" : undefined}
            data-dot={dot}
            style={{ top }}
            tabIndex={idx === roving ? 0 : -1}
            aria-haspopup="menu"
            aria-expanded={popoverOrd === section.ord}
            aria-label={`Section ${section.ord + 1}: ${section.heading || "Lead"}, version ${seq}${staleSuffix}`}
            onClick={(e) => openPopover(section.ord, e.currentTarget)}
            onKeyDown={(e) => onGripKeyDown(e, section.ord, idx)}
            onMouseEnter={() => {
              clearClose();
              setHoveredOrd(section.ord);
            }}
            onMouseLeave={scheduleClose}
            onFocus={() => {
              setFocusedOrd(section.ord);
              setRoving(idx);
            }}
            onBlur={() => setFocusedOrd(null)}
          >
            <GripVertical size={16} strokeWidth={1.5} aria-hidden="true" />
            {dot !== "none" ? (
              <span className={`wb-shandle__dot wb-shandle__dot--${dot}`} aria-hidden="true" />
            ) : null}
            <span
              className="wb-shandle__ver"
              data-testid={`section-handle-ver-${kind}-${idx}`}
              aria-hidden="true"
            >
              v{seq}
            </span>
          </button>
        );
      })}

      {popoverRegion != null && anchorRect != null ? (
        <SectionHandlePopover
          section={popoverRegion.section}
          kind={kind}
          index={placed.findIndex((r) => r.section.ord === popoverOrd)}
          anchorRect={anchorRect}
          link={linkFor(popoverRegion.section, kind, links)}
          fallbackSeq={headSeq(popoverRegion.section)}
          onCompare={() => onCompareSection(popoverRegion.section)}
          onRepin={onRepin}
          onCounterpart={onLinkCounterpart}
          onComment={() => onCommentSection(popoverRegion.section)}
          onClose={closePopover}
        />
      ) : null}
    </div>
  );
}
